import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, Printer, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { facultyData } from './facultyData';
import { AcademicTemplate } from './templates/AcademicTemplate';
import { ResearchTemplate } from './templates/ResearchTemplate';
import { StandardTemplate } from './templates/StandardTemplate';
interface CVPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: 'academic' | 'research' | 'standard';
  showQR: boolean;
  showPageNumbers: boolean;
}
export function CVPreviewModal({
  isOpen,
  onClose,
  template,
  showQR,
  showPageNumbers
}: CVPreviewModalProps) {
  const [isGenerating, setIsGenerating] = useState(true);
  useEffect(() => {
    if (isOpen) {
      setIsGenerating(true);
      const timer = setTimeout(() => {
        setIsGenerating(false);
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, template]);
  const handleDownload = () => {
    toast.success('PDF downloaded successfully');
  };
  const handlePrint = () => {
    window.print();
  };
  const renderTemplate = () => {
    switch (template) {
      case 'academic':
        return <AcademicTemplate data={facultyData} showQR={showQR} showPageNumbers={showPageNumbers} data-id="element-19" />;
      case 'research':
        return <ResearchTemplate data={facultyData} showQR={showQR} showPageNumbers={showPageNumbers} data-id="element-20" />;
      case 'standard':
        return <StandardTemplate data={facultyData} showQR={showQR} showPageNumbers={showPageNumbers} data-id="element-21" />;
      default:
        return <AcademicTemplate data={facultyData} showQR={showQR} showPageNumbers={showPageNumbers} data-id="element-22" />;
    }
  };
  return <AnimatePresence data-id="element-23">
      {isOpen && <div className="fixed inset-0 z-[100] flex flex-col items-center justify-start print:static print:z-auto print:block print:bg-white" data-id="element-24">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-900/80 print:hidden" onClick={onClose} data-id="element-25" />

          <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} className="relative w-full max-w-5xl h-[95vh] mt-[2.5vh] flex flex-col bg-slate-100 rounded-2xl shadow-2xl overflow-hidden print:h-auto print:mt-0 print:shadow-none print:bg-white print:rounded-none" data-id="element-26">
            {/* Top Bar - Hidden when printing */}
            <div className="bg-navy p-4 flex justify-between items-center shrink-0 print:hidden" data-id="element-27">
              <div className="text-white font-bold flex items-center" data-id="element-28">
                <span className="capitalize" data-id="element-29">{template}</span> CV Preview
              </div>
              <div className="flex items-center space-x-3" data-id="element-30">
                <button onClick={handlePrint} className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center" data-id="element-31">
                  <Printer className="w-4 h-4 mr-2" data-id="element-32" /> Print
                </button>
                <button onClick={handleDownload} className="bg-gold hover:bg-gold-hover text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors flex items-center" data-id="element-33">
                  <Download className="w-4 h-4 mr-2" data-id="element-34" /> Download PDF
                </button>
                <div className="w-px h-6 bg-white/20 mx-2" data-id="element-35" />
                <button onClick={onClose} className="text-white/50 hover:text-white p-1 transition-colors" data-id="element-36">
                  <X className="w-6 h-6" data-id="element-37" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-grow overflow-y-auto p-8 flex flex-col items-center print:p-0 print:overflow-visible" data-id="element-38">
              {isGenerating ? <div className="flex flex-col items-center justify-center h-full text-slate-500 print:hidden" data-id="element-39">
                  <Loader2 className="w-10 h-10 animate-spin text-navy mb-4" data-id="element-40" />
                  <p className="font-medium" data-id="element-41">
                    Generating your professional CV...
                  </p>
                </div> : <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="w-full max-w-[820px] bg-white shadow-xl min-h-[1160px] relative print:shadow-none print:min-h-0" data-id="element-42">
                  {/* University Branded Header */}
                  <div className="bg-navy text-white px-12 py-6 flex justify-between items-center border-b-4 border-gold" data-id="element-43">
                    <div className="flex items-center space-x-4" data-id="element-44">
                      <div className="w-12 h-12 bg-white rounded-lg p-1 flex items-center justify-center" data-id="element-45">
                        <img src="https://cdn.magicpatterns.com/uploads/b1kcBeF9jP1wXprpSnfWGK/image_30.png" alt="EWU Logo" className="w-full h-full object-contain" data-id="element-46" />
                      </div>
                      <div data-id="element-47">
                        <div className="font-bold text-xl tracking-wide" data-id="element-48">
                          EAST WEST UNIVERSITY
                        </div>
                        <div className="text-xs text-white/70 uppercase tracking-widest" data-id="element-49">
                          Excellence in Education
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* CV Content Area */}
                  <div className="p-12" data-id="element-50">{renderTemplate()}</div>
                </motion.div>}
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}